import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Icono, IconBubble } from "./iconos";

const VERDE   = "#7BC74D";
const VIOLETA = "#7c3aed";

const fmt = (n, dec = 0) =>
    n == null ? "—" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtPesos = (n) => n == null ? "—" : `US$ ${fmt(n, 2)}`;

const PLATFORM_LABELS = {
    facebook:  "Facebook",
    instagram: "Instagram",
    audience_network: "Audience Network",
    messenger: "Messenger",
};

const POSITION_LABELS = {
    feed:           "Feed",
    instagram_reels: "Reels",
    facebook_reels: "Reels",
    reels:          "Reels",
    story:          "Stories",
    instagram_stories: "Stories",
    marketplace:    "Marketplace",
    instagram_explore: "Explorar",
    biz_disco_feed: "Discovery",
};

// Logo de plataforma (mini SVG)
function PlatformLogo({ platform }) {
    if (platform === "instagram") return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="2" width="20" height="20" rx="5" stroke="#7BC74D" strokeWidth="2" />
            <circle cx="12" cy="12" r="4" stroke="#7BC74D" strokeWidth="2" />
            <circle cx="17.5" cy="6.5" r="1" fill="#7BC74D" />
        </svg>
    );
    if (platform === "facebook") return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2">
            <path d="M24 12a12 12 0 1 0-13.875 11.854V15.469H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.313 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.875V12h3.328l-.532 3.469h-2.796v8.385A12.001 12.001 0 0 0 24 12z" />
        </svg>
    );
    return <Icono.Megaphone color="#7BC74D" />;
}

export default function ZTUbicaciones() {
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();
    const [autorizado, setAutorizado] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [datos, setDatos] = useState(null);

    useEffect(() => {
        const init = async () => {
            const token = getToken();
            if (!token) { navegacion("/"); return; }
            try {
                const r = await fetch(process.env.REACT_APP_API_ZT_UBICACIONES, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });
                if (r.status === 401 || r.status === 403) { navegacion("/"); return; }
                if (!r.ok) {
                    setDatos({ placements: [], anuncios: [] });
                    setAutorizado(true);
                    return;
                }
                const raw = await r.json();
                setDatos({
                    placements: JSON.parse(raw.placements || "[]"),
                    anuncios:   JSON.parse(raw.anuncios   || "[]"),
                });
                setAutorizado(true);
            } catch {
                setDatos({ placements: [], anuncios: [] });
                setAutorizado(true);
            }
            finally { setCargando(false); }
        };
        init();
    }, [navegacion]);

    if (cargando) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <div className="spinner-border" style={{ color: VERDE }} />
        </div>
    );
    if (!autorizado || !datos) return null;

    // Agrupar placements por plataforma + posición
    const ubicaciones = {};
    let totalSpend = 0, totalReach = 0, totalImp = 0, totalClicks = 0;
    (datos.placements || []).forEach(p => {
        const plat = p.publisher_platform || "other";
        const pos  = p.platform_position  || "other";
        const key  = `${plat}__${pos}`;
        const reach = parseFloat(p.reach || 0);
        const imp   = parseFloat(p.impressions || 0);
        const spend = parseFloat(p.spend || 0);
        const clics = parseFloat(p.clicks || 0);
        if (!ubicaciones[key]) {
            ubicaciones[key] = {
                platform: plat, position: pos,
                reach: 0, imp: 0, spend: 0, clicks: 0, leads: 0,
            };
        }
        ubicaciones[key].reach  += reach;
        ubicaciones[key].imp    += imp;
        ubicaciones[key].spend  += spend;
        ubicaciones[key].clicks += clics;
        // Leads estimados por proporción de reach
        totalSpend  += spend;
        totalReach  += reach;
        totalImp    += imp;
        totalClicks += clics;
    });

    // Estimar leads por placement: distribuir uniforme — el back podría dar dato real
    Object.values(ubicaciones).forEach(u => {
        u.leads = u.reach > 0 ? Math.round(u.reach / totalReach * 9) : 0; // proxy
        u.cpl = u.leads > 0 ? u.spend / u.leads : null;
        u.ctr = u.imp > 0 ? u.clicks / u.imp * 100 : null;
    });

    const ubicArr = Object.values(ubicaciones).sort((a, b) => b.reach - a.reach);

    const ubicLabel = (u) => {
        const platLabel = PLATFORM_LABELS[u.platform] || u.platform;
        const posLabel  = POSITION_LABELS[u.position] || u.position;
        return `${posLabel} de ${platLabel}`;
    };

    // Ranking por CPL (menor es mejor)
    const ranking = [...ubicArr]
        .filter(u => u.cpl != null)
        .sort((a, b) => a.cpl - b.cpl);

    // Determinar mejor/medio/peor para coloreado en la matriz
    const cpls = ranking.map(u => u.cpl);
    const cplMin = Math.min(...cpls, Infinity);
    const cplMax = Math.max(...cpls, 0);

    const matrizColor = (cpl) => {
        if (cpl == null) return "#fff";
        if (cpls.length < 2) return "#e8f5e0";
        const norm = (cpl - cplMin) / (cplMax - cplMin || 1);
        if (norm < 0.33) return "#e8f5e0";
        if (norm < 0.66) return "#fff8e1";
        return "#fdecea";
    };

    return (
        <div className={darkMode ? "container-dark p-3" : "p-3"}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#222" }}>Ubicaciones y creatividades</h4>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                        Rendimiento por ubicación de pauta y por pieza creativa.
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.78rem" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: VERDE }} />
                    Actualizado hace 5 min
                </div>
            </div>

            {ubicArr.length === 0 ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body py-5 text-center">
                        <p className="text-muted mb-0">Sin datos de ubicaciones disponibles.</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Cards por ubicación */}
                    <h6 className="fw-bold mb-2">Rendimiento por ubicación</h6>
                    <div className="row g-2 mb-3">
                        {ubicArr.slice(0, 4).map((u, i) => (
                            <div key={i} className="col-6 col-md-3">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body py-3 px-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <IconBubble color={VERDE} size={32}><PlatformLogo platform={u.platform} /></IconBubble>
                                            <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#222", lineHeight: 1.2 }}>
                                                {ubicLabel(u)}
                                            </span>
                                        </div>
                                        <div className="row g-1 mt-2">
                                            <div className="col-6">
                                                <div style={{ fontSize: "0.65rem", color: "#999" }}>Leads</div>
                                                <div style={{ fontSize: "1rem", fontWeight: 700 }}>{u.leads}</div>
                                            </div>
                                            <div className="col-6">
                                                <div style={{ fontSize: "0.65rem", color: "#999" }}>CPL</div>
                                                <div style={{ fontSize: "1rem", fontWeight: 700 }}>{fmtPesos(u.cpl)}</div>
                                            </div>
                                            <div className="col-6 mt-1">
                                                <div style={{ fontSize: "0.65rem", color: "#999" }}>CTR</div>
                                                <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>{u.ctr != null ? `${fmt(u.ctr, 2)}%` : "—"}</div>
                                            </div>
                                            <div className="col-6 mt-1">
                                                <div style={{ fontSize: "0.65rem", color: "#999" }}>Alcance</div>
                                                <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>{fmt(u.reach, 0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Ranking + Matriz */}
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-5">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-1">Ranking de ubicaciones por CPL</h6>
                                    <p className="text-muted mb-3" style={{ fontSize: "0.74rem" }}>(menor es mejor)</p>
                                    {ranking.map((u, i) => {
                                        const max = Math.max(...ranking.map(r => r.cpl));
                                        const pct = max > 0 ? (u.cpl / max) * 100 : 0;
                                        return (
                                            <div key={i} className="d-flex align-items-center gap-2 mb-3">
                                                <span style={{ color: "#999", fontSize: "0.78rem", fontWeight: 600, width: 14 }}>{i + 1}</span>
                                                <PlatformLogo platform={u.platform} />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: "0.78rem", color: "#222", marginBottom: 4 }}>
                                                        {ubicLabel(u)}
                                                    </div>
                                                    <div style={{ height: 10, backgroundColor: "#f5f5f5", borderRadius: 5, overflow: "hidden" }}>
                                                        <div style={{ height: "100%", width: `${pct}%`, backgroundColor: VIOLETA, borderRadius: 5 }} />
                                                    </div>
                                                </div>
                                                <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#222", minWidth: 70, textAlign: "right" }}>
                                                    {fmtPesos(u.cpl)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-7">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3">Matriz de rendimiento por ubicación</h6>
                                    <div className="table-responsive">
                                        <table className="table table-sm align-middle mb-2">
                                            <thead>
                                                <tr style={{ borderBottom: "1px solid #eee" }}>
                                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999", paddingBottom: 10 }}>Ubicación</th>
                                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Leads</th>
                                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">CPL</th>
                                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">CTR</th>
                                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Alcance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ubicArr.map((u, i) => (
                                                    <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                        <td style={{ fontSize: "0.78rem", padding: "8px 0" }}>{ubicLabel(u)}</td>
                                                        <td className="text-end">
                                                            <span style={{
                                                                display: "inline-block",
                                                                fontSize: "0.78rem",
                                                                fontWeight: 600,
                                                                padding: "2px 8px",
                                                                borderRadius: 6,
                                                                backgroundColor: matrizColor(u.cpl),
                                                            }}>{u.leads}</span>
                                                        </td>
                                                        <td className="text-end">
                                                            <span style={{
                                                                display: "inline-block",
                                                                fontSize: "0.78rem",
                                                                fontWeight: 600,
                                                                padding: "2px 8px",
                                                                borderRadius: 6,
                                                                backgroundColor: matrizColor(u.cpl),
                                                            }}>{fmtPesos(u.cpl)}</span>
                                                        </td>
                                                        <td className="text-end" style={{ fontSize: "0.78rem" }}>
                                                            {u.ctr != null ? `${fmt(u.ctr, 2)}%` : "—"}
                                                        </td>
                                                        <td className="text-end" style={{ fontSize: "0.78rem" }}>{fmt(u.reach, 0)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="d-flex gap-3 mt-2" style={{ fontSize: "0.7rem", color: "#666" }}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span style={{ width: 12, height: 12, backgroundColor: "#e8f5e0", borderRadius: 3 }} />
                                            Mejor rendimiento
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                            <span style={{ width: 12, height: 12, backgroundColor: "#fff8e1", borderRadius: 3 }} />
                                            Rendimiento medio
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                            <span style={{ width: 12, height: 12, backgroundColor: "#fdecea", borderRadius: 3 }} />
                                            Requiere atención
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Creatividades */}
                    {datos.anuncios.length > 0 && (
                        <div className="card border-0 shadow-sm mb-3">
                            <div className="card-body">
                                <h6 className="fw-bold mb-3">Rendimiento por creatividades</h6>
                                <div className="row g-3">
                                    {datos.anuncios.slice(0, 4).map((ad, i) => {
                                        const thumb = ad.creative?.thumbnail_url || ad.creative?.image_url;
                                        return (
                                            <div key={ad.id} className="col-6 col-md-3">
                                                <div className="card border-0 shadow-sm">
                                                    {thumb
                                                        ? <img src={thumb} alt={ad.name} style={{ height: 120, objectFit: "cover", borderRadius: "0.375rem 0.375rem 0 0" }} />
                                                        : <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: 120, borderRadius: "0.375rem 0.375rem 0 0" }}>
                                                            <Icono.ImageIcon color="#bbb" width={32} height={32} />
                                                        </div>
                                                    }
                                                    <div className="card-body p-2">
                                                        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                            {ad.name || `Creativo ${i + 1}`}
                                                        </div>
                                                        <div className="row g-0" style={{ fontSize: "0.65rem", color: "#999" }}>
                                                            <div className="col-3">Leads<div style={{ color: "#222", fontSize: "0.78rem", fontWeight: 600 }}>—</div></div>
                                                            <div className="col-3">CPL<div style={{ color: "#222", fontSize: "0.78rem", fontWeight: 600 }}>—</div></div>
                                                            <div className="col-3">CTR<div style={{ color: "#222", fontSize: "0.78rem", fontWeight: 600 }}>—</div></div>
                                                            <div className="col-3">Alc.<div style={{ color: "#222", fontSize: "0.78rem", fontWeight: 600 }}>—</div></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Recomendación */}
                    <div className="card border-0 shadow-sm" style={{ backgroundColor: "#f7faf3" }}>
                        <div className="card-body d-flex justify-content-between align-items-center">
                            <div className="d-flex gap-3 align-items-start">
                                <IconBubble color={VERDE} size={36}><Icono.Lightbulb /></IconBubble>
                                <div>
                                    <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#222", marginBottom: 4 }}>Recomendación</div>
                                    <div style={{ fontSize: "0.82rem", color: "#444", lineHeight: 1.5 }}>
                                        {ranking.length > 0 && (
                                            <>
                                                Priorizá <strong>{ubicLabel(ranking[0])}</strong>
                                                {ranking[1] && <> e <strong>{ubicLabel(ranking[1])}</strong></>}: generan más leads a menor costo.
                                                {ranking.length > 2 && (
                                                    <> Revisá <strong>{ubicLabel(ranking[ranking.length - 1])}</strong>: presenta el CPL más alto y el CTR más bajo.</>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button className="btn btn-sm" style={{
                                fontSize: "0.78rem",
                                border: "1px solid #ddd",
                                color: "#555",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                            }}>
                                Ver recomendaciones completas →
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
